import { store } from '../store';
import { setConfig, setStatus, setLoading, setError } from '../store/slices/imSlice';
import { DEFAULT_IM_CONFIG, DEFAULT_IM_STATUS, type IMGatewayConfig, type IMGatewayStatus, type IMPlatform, type IMConnectivityTestResult } from '../types/im';

/** IM 页面状态协调器；登录/实例代次隔离所有迟到回包，操作确认后才刷新。 */
export class IMService {
  private generation = 0;
  private requestSequence = 0;
  private instanceId = 'default';
  private statusUnsubscribe: (() => void) | null = null;
  private mutationLocked = false;

  /** 切实例先清除旧凭据和状态，再注册仅属于新实例的事件。 */
  async init(instanceId = 'default'): Promise<void> {
    this.destroy();
    this.instanceId = instanceId;
    const generation = this.generation;
    this.statusUnsubscribe = window.electron.im.onStatusChange(status => {
      if (generation === this.generation) store.dispatch(setStatus(status));
    }, instanceId);
    await this.loadConfig();
    if (generation === this.generation) await this.loadStatus();
  }

  /** 销毁只取消页面订阅，不停止服务端机器人；同步清除凭据，迟到写入无效。 */
  destroy(): void {
    this.generation++;
    this.requestSequence++;
    this.statusUnsubscribe?.(); this.statusUnsubscribe = null;
    this.mutationLocked = false;
    store.dispatch(setConfig(structuredClone(DEFAULT_IM_CONFIG)));
    store.dispatch(setStatus(structuredClone(DEFAULT_IM_STATUS)));
    store.dispatch(setLoading(false));
    store.dispatch(setError(null));
  }

  /** 最新读取优先，切换实例或账号后不再向共享 Redux 写旧凭据。 */
  async loadConfig(): Promise<IMGatewayConfig | null> {
    const generation = this.generation, request = ++this.requestSequence, id = this.instanceId;
    const current = () => generation === this.generation && request === this.requestSequence;
    store.dispatch(setLoading(true));
    try {
      const result = await window.electron.im.getConfig(id);
      if (!current()) return null;
      if (!result.success || !result.config) throw new Error(result.error || '读取 IM 配置失败');
      const config = { ...structuredClone(DEFAULT_IM_CONFIG), ...result.config };
      store.dispatch(setConfig(config)); return config;
    } catch (error) {
      if (current()) store.dispatch(setError(error instanceof Error ? error.message : '读取 IM 配置失败'));
      return null;
    } finally { if (current()) store.dispatch(setLoading(false)); }
  }

  /** 状态只来自服务端连接，不凭配置中的 enabled 推断在线。 */
  async loadStatus(): Promise<IMGatewayStatus | null> {
    const generation = this.generation;
    const result = await window.electron.im.getStatus(this.instanceId).catch(() => null);
    if (generation !== this.generation || !result?.success || !result.status) return null;
    const status = { ...structuredClone(DEFAULT_IM_STATUS), ...result.status };
    store.dispatch(setStatus(status)); return status;
  }

  /** 同一页面写入同步互斥；失败保持输入，不自动重试网络副作用。 */
  private async mutate<T>(operation: (id: string) => Promise<T>): Promise<T | null> {
    if (this.mutationLocked) return null;
    this.mutationLocked = true;
    const generation = this.generation, id = this.instanceId;
    store.dispatch(setLoading(true)); store.dispatch(setError(null));
    try {
      const value = await operation(id);
      return generation === this.generation ? value : null;
    } catch (error) {
      if (generation === this.generation) store.dispatch(setError(error instanceof Error ? error.message : 'IM 操作失败'));
      return null;
    } finally {
      if (generation === this.generation) { this.mutationLocked = false; store.dispatch(setLoading(false)); }
    }
  }

  /** 保存失败不刷新覆盖用户草稿；调用方可显式重试。 */
  async updateConfig(config: Partial<IMGatewayConfig>): Promise<boolean> {
    const result = await this.mutate(async id => {
      const result = await window.electron.im.setConfig(config, id);
      if (!result.success) throw new Error(result.error || '保存 IM 配置失败');
      return true;
    });
    return result === true;
  }

  /** 启停成功后重新读取配置/状态，避免前端乐观切换掩盖失败。 */
  private async control(platform: IMPlatform, action: 'startGateway' | 'stopGateway'): Promise<boolean> {
    const result = await this.mutate(async id => {
      const result = await window.electron.im[action](platform, id);
      if (!result.success) throw new Error(result.error || 'IM 启停失败');
      return true;
    });
    if (result) { await this.loadConfig(); await this.loadStatus(); }
    return result === true;
  }

  /** 启动当前实例的平台连接，不能影响同平台其他实例。 */
  startGateway(platform: IMPlatform): Promise<boolean> { return this.control(platform, 'startGateway'); }
  /** 停用连接保留任务历史；关闭页面不会调用此方法。 */
  stopGateway(platform: IMPlatform): Promise<boolean> { return this.control(platform, 'stopGateway'); }

  /** 临时连通测试不保存配置，不把超时当作成功。 */
  async testGateway(platform: IMPlatform, configOverride?: Partial<IMGatewayConfig>): Promise<IMConnectivityTestResult | null> {
    return this.mutate(async id => {
      const result = await window.electron.im.testGateway(platform, configOverride, id);
      if (!result.success || !result.result) throw new Error(result.error || 'IM 连通测试失败');
      return result.result;
    });
  }

  /** 当前可见实例配置，只在页面操作中使用，禁止持久化凭据到 localStorage。 */
  getConfig(): IMGatewayConfig { return store.getState().im.config; }
  /** 当前可见实例的服务端连接状态。 */
  getStatus(): IMGatewayStatus { return store.getState().im.status; }
  /** 仅统计实际在线的平台状态。 */
  isAnyConnected(): boolean { return Object.values(this.getStatus()).some(status => status.connected); }
}

export const imService = new IMService();
window.addEventListener('auth:identityChanging', () => imService.destroy());
